import React from 'react';
import { List, ListItem } from '@xingternal/list';
import { Link } from 'react-router-dom';

import { Version, VersionStatus, Reason } from '../../../../models/version';
import { AppRoutes } from '../../../../RoutesConfig';
import { Page } from '../../../../models/page';
import styles from './styles.css';

export interface PageVersionsProps {
  versions: Version[];
  page: Page;
  currentVersion: Version;
  currentGenericVersionId?: number;
  locale: string;
}

const returnUrl = (page: Page, version: Version, locale: string): string => {
  let genericVersionParam = null;
  if (version.reason == Reason.UpdatedByGeneric) {
    genericVersionParam = version.genericId;
  }

  return AppRoutes.admin.adminTermsOverview(
    page.slug,
    version.id,
    locale,
    genericVersionParam
  );
};

const sortVersions = (versions: Version[]): Version[] =>
  [...versions].sort((version1, version2) => {
    if (
      version1.status == VersionStatus.draft &&
      version2.status != VersionStatus.draft
    ) {
      return -1;
    }

    if (
      version1.status != VersionStatus.draft &&
      version2.status == VersionStatus.draft
    ) {
      return 1;
    }

    return (
      new Date(version2.liveDate).getTime() -
      new Date(version1.liveDate).getTime()
    );
  });

export const PageVersions = ({
  versions,
  currentVersion,
  page,
  locale,
}: PageVersionsProps): React.ReactElement => {
  const formatDate = (str: string): string => {
    const date = new Date(str).toLocaleDateString(
      navigator.language || 'de-DE',
      {
        hour: '2-digit',
        minute: '2-digit',
      }
    );
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return `${date} ${timezone}`;
  };

  const listItemClass = (version: Version): string => {
    if (version == currentVersion) {
      return styles.versionListItemActive;
    }

    return styles.versionListItem;
  };

  const returnStatus = (version: Version): string =>
    (version.status == VersionStatus.scheduled && '(Scheduled)') ||
    (version.status == VersionStatus.draft && '(Draft)') ||
    (version.reason == Reason.UpdatedByGeneric && `(${version.reason})`) ||
    (version.id == page.publishedVersionId && `(Published, ${version.reason})`);

  return (
    <List>
      <ListItem className={styles.pageName} key={page.name}>
        {page.name}
      </ListItem>

      {sortVersions(versions).map(version => (
        <Link
          key={[version.id, version.liveDate, version.genericId].join('-')}
          className={styles.link}
          to={returnUrl(page, version, locale)}
        >
          <ListItem className={listItemClass(version)}>
            {formatDate(version.liveDate)} {returnStatus(version)}
          </ListItem>
        </Link>
      ))}
    </List>
  );
};
